//@flow

import * as React from 'react';
import { themr } from 'react-css-themr';
import classnames from 'classnames';

import {
    SelectComponentTypes,
    selectFactory,
    SelectTypes
} from 'common/components/core/select';
import DefaultSelectComponents from 'common/components/core/select/DefaultSelectComponents';
import theme from './HomebaseSelector.scss';
import { ListCard } from 'common/components/core/card/ListCard';
import { memoized } from 'common/utils/Memoizer';
import { dashboardColors } from 'common/enums/ColorEnum';
import HomebaseChip from 'common/components/core/common/HomebaseChip';

import type { Geometry } from 'common/models/internal/Geometry';
import type { Homebase } from 'common/models/internal/Homebase';
import type { List } from 'immutable';

type Props = {
    selectedAddress: string,
    homebases: List<Homebase>,
    theme: Object,
    className?: string,
    openMenuOnFocus?: boolean,
    autoFocus?: boolean,
    isClearable?: boolean,
    isSearchable?: boolean,
    cache?: boolean,
    selectType?: string,
    placeholder?: string,
    placeholderComponent?: React.Element<any>,
    shouldKeyDownEventCreateNewOption: () => void,
    formatCreateLabel: () => string | any,
    onCreateOption: (i: string) => void,
    onFocus: () => void,
    onBlur: () => void,
    onChange: () => void,
    testId?: string
};

type RendererParams = {
    data: { value: string, label: string, geometry?: Geometry },
    theme: Object
};
const HomebaseRenderer = ({
    data,
    selectProps,
    ...others
}: RendererParams): React.Element<any> => {
    const { showHomebaseIcon } = selectProps;
    let leftIcon = null;
    if (showHomebaseIcon) {
        if (data.geometry) {
            leftIcon = 'homebase';
        } else {
            leftIcon = 'location-icon';
        }
    }
    return <ListCard {...others} leftIcon={leftIcon} />;
};

export const homebaseSelectorFactory = options => {
    const Select = selectFactory({
        ...options,
        Option:
            options.Option ||
            DefaultSelectComponents.optionFactory(HomebaseRenderer),
        SingleValue:
            options.SingleValue ||
            DefaultSelectComponents.singleValueFactory(HomebaseRenderer)
    });

    class HomebaseSelector extends React.PureComponent<Props, void> {
        @memoized()
        getOptions(homebases) {
            const options = [];
            if (!homebases) return options;
            homebases.forEach(homebase => {
                options.push({
                    value: homebase.id,
                    label: homebase.getAddressString(),
                    geometry: homebase.geometry,
                    tat: homebase.tat,
                    testId: homebase.id
                });
            });
            return options;
        }

        onChange = (value: {
            label: string,
            value: string,
            geometry: Geometry,
            tat: number
        }) => {
            this.props.onChange(value);
        };

        @memoized()
        getValue(selectedAddress, homebases) {
            const matchingOption = this.getOptions(homebases).find(option => {
                return option.label === selectedAddress;
            });
            if (matchingOption) {
                return matchingOption.value;
            }
            return selectedAddress;
        }

        render() {
            const {
                theme,
                className,
                openMenuOnFocus,
                autoFocus,
                isClearable,
                isSearchable,
                cache,
                shouldKeyDownEventCreateNewOption,
                formatCreateLabel,
                onCreateOption,
                selectType,
                onBlur,
                homebases,
                selectedAddress,
                showHomebaseIcon,
                testId,
                ...others
            } = this.props;
            return (
                <Select
                    {...others}
                    showHomebaseIcon={showHomebaseIcon}
                    theme={theme}
                    className={classnames(theme.homebaseSelector, className)}
                    selectType={selectType}
                    formatCreateLabel={formatCreateLabel}
                    shouldKeyDownEventCreateNewOption={
                        shouldKeyDownEventCreateNewOption
                    }
                    onChange={this.onChange}
                    onCreateOption={onCreateOption}
                    options={this.getOptions(homebases)}
                    openMenuOnFocus={openMenuOnFocus}
                    autoFocus={autoFocus}
                    isClearable={isClearable}
                    isSearchable={isSearchable}
                    cache={cache}
                    onBlur={onBlur}
                    value={this.getValue(selectedAddress, homebases)}
                    testId={testId}
                />
            );
        }
    }
    HomebaseSelector.defaultProps = {
        cache: false,
        openMenuOnFocus: true
    };
    return themr('HomebaseSelector', theme)(HomebaseSelector);
};

const MultiValueLabel = ({ data, selectProps, innerProps }) => {
    const { theme } = selectProps;
    return (
        <HomebaseChip
            className={theme.homebaseChip}
            theme={theme}
            homebaseName={data.label}
            backgroundColor={dashboardColors.transparent.name}
            noPadding
            {...innerProps}
        />
    );
};
const MultiHomebaseSelectComponent = selectFactory({ MultiValueLabel });
class MultiHomebaseSelect extends React.PureComponent {
    @memoized()
    getHomebaseDropdownOptions(homebases) {
        const options = [];
        if (!homebases) return options;
        homebases.forEach(homebase => {
            options.push({
                value: homebase.id,
                label: homebase.getPlaceName() || homebase.getAddressString(),
                geometry: homebase.geometry,
                tat: homebase.tat
            });
        });
        return options;
    }
    @memoized()
    getValue(selectedHomebases) {
        return (selectedHomebases || []).map(homebase => homebase.value);
    }
    render() {
        const {
            theme,
            className,
            homebases,
            selectedHomebases,
            placeholder,
            ...others
        } = this.props;
        const values = this.getValue(selectedHomebases);
        return (
            <MultiHomebaseSelectComponent
                {...others}
                theme={theme}
                className={classnames(theme.homebaseSelector, theme.dropdown, {
                    [className]: !!className
                })}
                selectType={SelectTypes.Outline}
                options={this.getHomebaseDropdownOptions(homebases)}
                isMulti
                openMenuOnFocus
                blurInputOnSelect={false}
                isSearchable
                closeMenuOnSelect={false}
                placeholder={placeholder}
                value={values}
            />
        );
    }
}

const HomebaseSelectorComponent = homebaseSelectorFactory({});
const CreatableHomebaseSelector = homebaseSelectorFactory({
    selectComponentType: SelectComponentTypes.Creatable
});
export default HomebaseSelectorComponent;
export {
    CreatableHomebaseSelector,
    HomebaseSelectorComponent as HomebaseSelector,
    MultiHomebaseSelect
};
